import { useState } from 'react';
import { useAssignPlan } from '../hooks/usePlanAssignment';
import type { BillingCycle, ExceededResource } from '../types/tenants.types';
import { usePlans } from '@/features/plans/hooks/usePlans';
import { getVisibilityLabel } from '@/features/plans/utils/plan-sync-status';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';

interface AssignPlanModalProps {
  tenantId: string;
  tenantName: string;
  open: boolean;
  onClose: () => void;
}

const RESOURCE_LABELS: Record<string, string> = {
  Users: 'Miembros',
  Regions: 'Regiones',
  ActiveTopics: 'Temas activos',
  ActiveActors: 'Actores',
  ActiveAlerts: 'Alertas',
};

const BILLING_CYCLES: { value: BillingCycle; label: string }[] = [
  { value: 'Monthly', label: 'Mensual' },
  { value: 'Annual', label: 'Anual' },
];

export function AssignPlanModal({ tenantId, tenantName, open, onClose }: AssignPlanModalProps) {
  const { plans } = usePlans();
  const { assignPlan, isAssigning } = useAssignPlan(tenantId);
  const [planId, setPlanId] = useState<string>('');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('Monthly');
  // Lo que la cuenta ya usa por encima del plan elegido. Mientras haya algo, el siguiente
  // "Asignar" va forzado: el administrador ya lo vio y decidió.
  const [exceeded, setExceeded] = useState<ExceededResource[] | null>(null);

  // Un plan sin sincronizar no tiene precios en Stripe, asi que la API lo rechaza con
  // Plan.NotSynced. Ofrecerlo solo llevaria al administrador a un error evitable.
  const assignablePlans = plans.filter((plan) => plan.isOfferable);

  const handleConfirm = () => {
    if (!planId) return;

    assignPlan(
      { planId, billingCycle, force: exceeded !== null },
      {
        onSuccess: () => {
          Notification.success({ title: 'Plan asignado exitosamente' });
          setPlanId('');
          setExceeded(null);
          onClose();
        },
        onError: (error) => {
          const data = (
            error as {
              response?: { data?: { title?: string; exceededResources?: ExceededResource[] } };
            }
          ).response?.data;
          if (data?.title === 'Subscription.AssignPlanExceedsLimits') {
            setExceeded(data.exceededResources ?? []);
          }
        },
      },
    );
  };

  const handleClose = () => {
    if (!isAssigning) {
      setPlanId('');
      setExceeded(null);
      onClose();
    }
  };

  // Otro plan tiene otros topes: lo que excedía ya no aplica.
  const handlePlanChange = (value: string) => {
    setPlanId(value);
    setExceeded(null);
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(v) => !v && handleClose()}
      title="Asignar plan"
      description={`El plan se otorga a "${tenantName}" sin pasar por checkout. La cuenta adquiere sus condiciones igual que si lo hubiera contratado.`}
      confirmLabel={exceeded ? 'Asignar de todos modos' : 'Asignar'}
      cancelLabel="Cancelar"
      onConfirm={handleConfirm}
      isPending={isAssigning}
      data-testid="assign-plan-dialog"
    >
      <div className="flex flex-col gap-4">
        <SelectInput
          label="Plan"
          value={planId}
          onValueChange={handlePlanChange}
          disabled={isAssigning}
          placeholder="Seleccionar plan"
          data-testid="assign-plan-select"
        >
          {assignablePlans.map((plan) => (
            <SelectItem key={plan.id} value={plan.id}>
              {plan.name} · {getVisibilityLabel(plan.visibility)}
            </SelectItem>
          ))}
        </SelectInput>

        <SelectInput
          label="Ciclo de cobro"
          value={billingCycle}
          onValueChange={(value) => setBillingCycle(value as BillingCycle)}
          disabled={isAssigning}
          placeholder="Seleccionar ciclo"
          data-testid="assign-billing-cycle-select"
        >
          {BILLING_CYCLES.map((cycle) => (
            <SelectItem key={cycle.value} value={cycle.value}>
              {cycle.label}
            </SelectItem>
          ))}
        </SelectInput>

        {exceeded && (
          <div
            data-testid="assign-plan-exceeded"
            className="flex flex-col gap-2 rounded-lg border border-warn/30 bg-warn/10 p-3"
          >
            <p className="text-warn text-xs">
              La cuenta ya usa más de lo que permite este plan. No se le borra nada, pero no podrá
              crecer en estos recursos hasta bajar al tope.
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-primary">
                  <th className="text-left font-medium">Recurso</th>
                  <th className="text-right font-medium">Actual</th>
                  <th className="text-right font-medium">Límite</th>
                </tr>
              </thead>
              <tbody>
                {exceeded.map((r) => (
                  <tr key={r.resource} className="text-light">
                    <td>{RESOURCE_LABELS[r.resource] ?? r.resource}</td>
                    <td className="text-right text-negative tabular-nums">{r.current}</td>
                    <td className="text-right tabular-nums">{r.limit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {assignablePlans.length === 0 && (
          <p data-testid="no-assignable-plans" className="text-warn text-xs">
            No hay planes sincronizados con Stripe. Sin precios no hay con qué cobrarle a la cuenta.
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
